import { useEffect, useState } from 'react';

import type { Battle, BattleComment, CommentPage } from '../types';
import { SponsoredSlot } from './SponsoredSlot';

const reportReasons = [
  ['spam', 'Spam'],
  ['harassment', 'Acoso'],
  ['hate', 'Odio'],
  ['sexual', 'Contenido sexual'],
  ['violence', 'Violencia'],
  ['other', 'Otro motivo'],
] as const;

type ReportReason = (typeof reportReasons)[number][0];

export function BattleComments({
  battle,
  authenticated,
}: {
  battle: Battle;
  authenticated: boolean;
}) {
  const [opened, setOpened] = useState(false);
  const [comments, setComments] = useState<BattleComment[]>([]);
  const [nextBefore, setNextBefore] = useState<number | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [canComment, setCanComment] = useState(false);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [content, setContent] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [reportingId, setReportingId] = useState<string | null>(null);
  const [reason, setReason] = useState<ReportReason>('spam');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [liveConnected, setLiveConnected] = useState(false);

  useEffect(() => {
    if (!opened) return;
    let stopped = false;
    let socket: WebSocket | null = null;
    let reconnectTimer: number | null = null;

    const connect = () => {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      socket = new WebSocket(
        `${protocol}//${window.location.host}/api/v1/battles/${battle.slug}/live`,
      );
      socket.addEventListener('open', () => setLiveConnected(true));
      socket.addEventListener('message', (event) => {
        if (typeof event.data !== 'string' || event.data === 'pong') return;
        try {
          const update = JSON.parse(event.data) as
            | { type: 'connected' }
            | { type: 'comment.created' | 'comment.edited'; comment: BattleComment }
            | { type: 'comment.deleted'; commentId: string }
            | { type: 'battle.closed' };
          if (update.type === 'comment.created') {
            setComments((current) =>
              current.some((item) => item.id === update.comment.id)
                ? current
                : [{ ...update.comment, isMine: false }, ...current],
            );
          } else if (update.type === 'comment.edited') {
            setComments((current) =>
              current.map((item) =>
                item.id === update.comment.id ? { ...update.comment, isMine: item.isMine } : item,
              ),
            );
          } else if (update.type === 'comment.deleted') {
            setComments((current) => current.filter((item) => item.id !== update.commentId));
          } else if (update.type === 'battle.closed') {
            setCanComment(false);
            setMessage('La batalla terminó. La conversación queda disponible en modo lectura.');
          }
        } catch {
          // Un mensaje ajeno al protocolo de AGAM no modifica la interfaz.
        }
      });
      socket.addEventListener('close', () => {
        setLiveConnected(false);
        if (!stopped) reconnectTimer = window.setTimeout(connect, 3_000);
      });
      socket.addEventListener('error', () => socket?.close());
    };

    connect();
    return () => {
      stopped = true;
      if (reconnectTimer !== null) window.clearTimeout(reconnectTimer);
      socket?.close();
      setLiveConnected(false);
    };
  }, [battle.slug, opened]);

  async function load(before?: number) {
    setLoading(true);
    setMessage(null);
    try {
      const query = new URLSearchParams({ limit: '12' });
      if (before) query.set('before', String(before));
      const response = await fetch(`/api/v1/battles/${battle.slug}/comments?${query}`, {
        headers: { Accept: 'application/json' },
        cache: 'no-store',
      });
      if (!response.ok) throw new Error('No fue posible cargar los comentarios.');
      const body = (await response.json()) as CommentPage;
      setComments((current) => {
        const incoming = body.myComment
          ? [body.myComment, ...body.comments.filter((item) => item.id !== body.myComment?.id)]
          : body.comments;
        const combined = before ? [...current, ...incoming] : incoming;
        return combined.filter(
          (comment, index) => combined.findIndex((item) => item.id === comment.id) === index,
        );
      });
      setCanComment(body.canComment);
      setHasMore(body.page.hasMore);
      setNextBefore(body.page.nextBefore);
      setLoaded(true);
    } catch (cause) {
      setMessage(cause instanceof Error ? cause.message : 'No fue posible abrir la conversación.');
    } finally {
      setLoading(false);
    }
  }

  function toggle() {
    const next = !opened;
    setOpened(next);
    if (next && !loaded) void load();
  }

  async function publish(event: React.FormEvent) {
    event.preventDefault();
    setBusyId('new');
    setMessage(null);
    try {
      const response = await fetch(`/api/v1/battles/${battle.slug}/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ content }),
      });
      const body = (await response.json()) as { comment?: BattleComment; detail?: string };
      if (!response.ok || !body.comment) {
        throw new Error(body.detail ?? 'No fue posible publicar el comentario.');
      }
      setComments((current) => [body.comment!, ...current]);
      setCanComment(false);
      setContent('');
      setMessage('Comentario publicado.');
    } catch (cause) {
      setMessage(cause instanceof Error ? cause.message : 'No fue posible publicar.');
    } finally {
      setBusyId(null);
    }
  }

  async function edit(event: React.FormEvent, commentId: string) {
    event.preventDefault();
    setBusyId(commentId);
    setMessage(null);
    try {
      const response = await fetch(`/api/v1/battles/${battle.slug}/comments/${commentId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ content: editContent }),
      });
      const body = (await response.json()) as { comment?: BattleComment; detail?: string };
      if (!response.ok || !body.comment) {
        throw new Error(body.detail ?? 'No fue posible editar la opinión.');
      }
      setComments((current) =>
        current.map((comment) => (comment.id === commentId ? body.comment! : comment)),
      );
      setEditingId(null);
      setEditContent('');
      setMessage('Opinión editada. Ya utilizaste tu única edición.');
    } catch (cause) {
      setMessage(cause instanceof Error ? cause.message : 'No fue posible editar.');
    } finally {
      setBusyId(null);
    }
  }

  async function remove(commentId: string) {
    if (!window.confirm('¿Quieres borrar este comentario?')) return;
    setBusyId(commentId);
    setMessage(null);
    try {
      const response = await fetch(`/api/v1/battles/${battle.slug}/comments/${commentId}`, {
        method: 'DELETE',
        headers: { Accept: 'application/json' },
      });
      const body = (await response.json()) as { detail?: string };
      if (!response.ok) throw new Error(body.detail ?? 'No fue posible borrar el comentario.');
      setComments((current) => current.filter((comment) => comment.id !== commentId));
      setMessage('Comentario borrado.');
    } catch (cause) {
      setMessage(cause instanceof Error ? cause.message : 'No fue posible borrar.');
    } finally {
      setBusyId(null);
    }
  }

  async function report(commentId: string) {
    setBusyId(commentId);
    setMessage(null);
    try {
      const response = await fetch(`/api/v1/battles/${battle.slug}/comments/${commentId}/reports`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ reason }),
      });
      const body = (await response.json()) as { detail?: string };
      if (!response.ok) throw new Error(body.detail ?? 'No fue posible enviar el reporte.');
      setReportingId(null);
      setReason('spam');
      setMessage('Reporte recibido. Gracias por ayudar a cuidar la arena.');
    } catch (cause) {
      setMessage(cause instanceof Error ? cause.message : 'No fue posible reportar.');
    } finally {
      setBusyId(null);
    }
  }

  return (
    <section className="comments-panel mt-4 overflow-hidden rounded-2xl">
      <button
        type="button"
        onClick={toggle}
        className="flex w-full items-center justify-between gap-4 px-4 py-3 text-left"
        aria-expanded={opened}
      >
        <span className="flex-1">
          <strong className="block text-sm text-white">Comentarios</strong>
          <SponsoredSlot slot="battle_comments" compact />
        </span>
        <span className="flex items-center gap-2 text-xs text-zinc-500">
          {opened && (
            <span className={liveConnected ? 'text-emerald-300' : 'text-zinc-600'}>
              {liveConnected ? '● En vivo' : 'Reconectando…'}
            </span>
          )}
          {opened ? 'Cerrar' : 'Abrir conversación'}
        </span>
      </button>

      {opened && (
        <div className="border-t border-white/8 px-4 pb-4 pt-4">
          {battle.status === 'active' && authenticated && loaded && canComment && (
            <form onSubmit={publish} className="mb-4">
              <textarea
                className="field-input min-h-20 resize-y text-sm"
                value={content}
                onChange={(event) => setContent(event.target.value)}
                minLength={2}
                maxLength={500}
                required
                placeholder="Suma tu opinión sin enlaces…"
              />
              <div className="mt-2 flex items-center justify-between gap-3">
                <span className="text-[0.65rem] text-zinc-600">
                  {content.length}/500 · una opinión por batalla · sin enlaces
                </span>
                <button
                  disabled={busyId === 'new' || content.trim().length < 2}
                  className="rounded-full bg-violet-200 px-4 py-2 text-xs font-bold text-zinc-950 disabled:opacity-40"
                >
                  {busyId === 'new' ? 'Publicando…' : 'Comentar'}
                </button>
              </div>
            </form>
          )}

          {battle.status === 'active' && authenticated && loaded && !canComment && (
            <p className="mb-4 rounded-xl bg-white/[0.035] px-3 py-2 text-xs text-zinc-500">
              Ya publicaste tu única opinión en esta batalla. Puedes editarla una sola vez.
            </p>
          )}

          {battle.status === 'active' && !authenticated && (
            <p className="mb-4 rounded-xl bg-white/[0.035] px-3 py-2 text-xs text-zinc-500">
              Inicia sesión para participar. Puedes leer la conversación sin una cuenta.
            </p>
          )}
          {battle.status === 'closed' && (
            <p className="mb-4 rounded-xl bg-white/[0.035] px-3 py-2 text-xs text-zinc-500">
              La batalla terminó. Los comentarios permanecen visibles en modo lectura.
            </p>
          )}

          {message && (
            <p role="status" className="mb-3 text-xs leading-5 text-violet-200">
              {message}
            </p>
          )}

          {loading && !loaded ? (
            <p className="py-4 text-center text-xs text-zinc-600">Cargando conversación…</p>
          ) : comments.length === 0 ? (
            <p className="py-4 text-center text-xs text-zinc-600">
              Aún no hay comentarios. La conversación empieza aquí.
            </p>
          ) : (
            <div className="space-y-3">
              {comments.map((comment) => (
                <article
                  key={comment.id}
                  className="rounded-xl border border-white/8 bg-black/15 p-3"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <strong className="text-xs text-zinc-200">{comment.displayName}</strong>
                    <span className="rounded-full border border-white/10 px-2 py-0.5 text-[0.55rem] uppercase tracking-wider text-zinc-500">
                      {comment.accountType === 'artist' ? 'Artista' : 'Oyente'}
                    </span>
                    {comment.isParticipant && (
                      <span className="rounded-full bg-fuchsia-300/10 px-2 py-0.5 text-[0.55rem] font-bold uppercase tracking-wider text-fuchsia-200">
                        Contrincante
                      </span>
                    )}
                    <time
                      className="ml-auto text-[0.6rem] text-zinc-600"
                      dateTime={comment.createdAt}
                    >
                      {formatDate(comment.createdAt)}
                    </time>
                  </div>
                  {editingId === comment.id ? (
                    <form onSubmit={(event) => void edit(event, comment.id)} className="mt-3">
                      <textarea
                        className="field-input min-h-20 resize-y text-sm"
                        value={editContent}
                        onChange={(event) => setEditContent(event.target.value)}
                        minLength={2}
                        maxLength={500}
                        required
                        autoFocus
                      />
                      <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                        <span className="text-[0.65rem] text-zinc-600">
                          {editContent.length}/500 · esta edición es definitiva
                        </span>
                        <span className="flex gap-2">
                          <button
                            disabled={busyId === comment.id || editContent.trim().length < 2}
                            className="rounded-full bg-violet-200 px-3 py-1.5 text-xs font-bold text-zinc-950 disabled:opacity-40"
                          >
                            {busyId === comment.id ? 'Guardando…' : 'Guardar edición'}
                          </button>
                          <button
                            type="button"
                            onClick={() => setEditingId(null)}
                            className="px-2 text-xs text-zinc-600"
                          >
                            Cancelar
                          </button>
                        </span>
                      </div>
                    </form>
                  ) : (
                    <p className="mt-2 whitespace-pre-wrap break-words text-sm leading-5 text-zinc-400">
                      {comment.content}
                      {comment.edited && (
                        <span className="ml-2 text-[0.6rem] italic text-zinc-600">Editado</span>
                      )}
                    </p>
                  )}
                  {comment.isMine ? (
                    editingId !== comment.id && (
                      <div className="mt-2 flex gap-3">
                        {battle.status === 'active' && !comment.edited && (
                          <button
                            type="button"
                            onClick={() => {
                              setEditingId(comment.id);
                              setEditContent(comment.content);
                            }}
                            className="text-[0.65rem] text-zinc-600 hover:text-violet-200"
                          >
                            Editar una vez
                          </button>
                        )}
                        <button
                          type="button"
                          disabled={busyId === comment.id}
                          onClick={() => void remove(comment.id)}
                          className="text-[0.65rem] text-zinc-600 hover:text-rose-300 disabled:opacity-40"
                        >
                          Borrar
                        </button>
                      </div>
                    )
                  ) : authenticated && reportingId === comment.id ? (
                    <div className="mt-3 flex flex-wrap gap-2">
                      <select
                        value={reason}
                        onChange={(event) => setReason(event.target.value as ReportReason)}
                        className="rounded-lg border border-white/10 bg-zinc-950 px-2 py-1.5 text-xs text-zinc-300"
                        aria-label="Motivo del reporte"
                      >
                        {reportReasons.map(([value, label]) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        disabled={busyId === comment.id}
                        onClick={() => void report(comment.id)}
                        className="rounded-lg bg-rose-300/10 px-3 py-1.5 text-xs text-rose-200 disabled:opacity-40"
                      >
                        Enviar reporte
                      </button>
                      <button
                        type="button"
                        onClick={() => setReportingId(null)}
                        className="px-2 text-xs text-zinc-600"
                      >
                        Cancelar
                      </button>
                    </div>
                  ) : authenticated ? (
                    <button
                      type="button"
                      onClick={() => setReportingId(comment.id)}
                      className="mt-2 text-[0.65rem] text-zinc-600 hover:text-amber-200"
                    >
                      Reportar
                    </button>
                  ) : null}
                </article>
              ))}
            </div>
          )}

          {hasMore && nextBefore && (
            <button
              type="button"
              disabled={loading}
              onClick={() => void load(nextBefore)}
              className="mt-4 w-full rounded-full border border-white/10 px-4 py-2 text-xs text-zinc-400 disabled:opacity-40"
            >
              {loading ? 'Cargando…' : 'Cargar anteriores'}
            </button>
          )}
        </div>
      )}
    </section>
  );
}

function formatDate(value: string) {
  return new Intl.DateTimeFormat('es', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}
